'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/**
 * MilestoneBar — the DORMANT → ON FIRE score track, made interactive. Hovering
 * (or dragging on touch) anywhere across the bar/labels moves a preview handle to
 * the cursor and surfaces a tooltip explaining the band under it; on leave the
 * handle eases back to the real score. Each band label is also a focusable target
 * so keyboard users get the same explanation.
 */

interface Band {
  key: string;
  label: string;
  min: number;
  max: number;
  color: string;
  desc: string;
}

const BANDS: Band[] = [
  {
    key: 'dormant',
    label: 'Dormant',
    min: 0,
    max: 25,
    color: 'var(--muted-foreground)',
    desc: 'Little to no activity yet. Start applying and reaching out to warm things up.',
  },
  {
    key: 'warming',
    label: 'Warming',
    min: 25,
    max: 50,
    color: 'var(--status-warning)',
    desc: 'Momentum is building. Keep a steady daily pace to reach Active.',
  },
  {
    key: 'active',
    label: 'Active',
    min: 50,
    max: 75,
    color: 'var(--success-green)',
    desc: "You're consistently running your plan. Keep it up to hit On fire.",
  },
  {
    key: 'onfire',
    label: 'On fire',
    min: 75,
    max: 100,
    color: 'var(--alert-red)',
    desc: "Peak momentum — you're crushing your daily plan. Hold the streak.",
  },
];

function bandForPct(pct: number): Band {
  return BANDS.find((b) => pct < b.max) ?? BANDS[BANDS.length - 1];
}

const clampPct = (n: number) => Math.max(0, Math.min(100, n));

export function MilestoneBar({ score }: { score: number }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [hoverPct, setHoverPct] = useState<number | null>(null);
  // Viewport coords of the hovered point, so the tooltip can be portaled to the
  // body (escaping the card's overflow-hidden) and still sit over the handle.
  const [tip, setTip] = useState<{ x: number; top: number } | null>(null);
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setMounted(true), []);

  const previewing = hoverPct !== null;
  // Real value never moves; the ghost handle tracks the hover for preview only.
  const realPct = clampPct(score);
  const realBand = bandForPct(realPct);
  const activePct = clampPct(previewing ? hoverPct : score);
  const band = bandForPct(activePct);

  const previewAt = (pct: number) => {
    const p = clampPct(pct);
    setHoverPct(p);
    const el = trackRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setTip({ x: rect.left + (p / 100) * rect.width, top: rect.top });
    }
  };

  const moveTo = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    previewAt(((clientX - rect.left) / rect.width) * 100);
  };

  // Clamp the (fixed-positioned, body-portaled) tooltip's centre so its 13rem
  // width never runs off either side of the viewport.
  const TIP_HALF = 104;
  const viewW = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const tipX = tip ? Math.min(viewW - TIP_HALF - 8, Math.max(TIP_HALF + 8, tip.x)) : 0;

  return (
    <div
      className="relative select-none"
      onPointerMove={(e) => moveTo(e.clientX)}
      onPointerDown={(e) => moveTo(e.clientX)}
      onPointerLeave={() => setHoverPct(null)}
    >
      {/* Tooltip — portaled to <body> so the card's overflow never clips it, and
          fixed-positioned just above the hovered point. */}
      {mounted && previewing && tip &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[60] w-52 max-w-[calc(100vw-1rem)] -translate-x-1/2 -translate-y-full rounded-lg border border-border bg-popover p-2.5 text-popover-foreground"
            style={{ left: tipX, top: tip.top - 10, boxShadow: 'var(--shadow-warm-md)' }}
          >
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: band.color }} />
              <span className="text-label font-bold text-foreground">{band.label}</span>
              <span className="ml-auto text-2xs font-semibold tabular-nums text-muted-foreground">
                {band.min}–{band.max}
              </span>
            </div>
            <p className="mt-1 text-caption leading-snug text-muted-foreground">{band.desc}</p>
          </div>,
          document.body,
        )}

      {/* Track */}
      <div
        ref={trackRef}
        className="relative mt-1 h-2 cursor-default rounded-full"
        style={{ background: 'var(--muted)' }}
      >
        {/* Real fill + handle — pinned to the actual score; hovering never moves
            these, so the bar reads as informational, not a control you're setting. */}
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${Math.max(2, realPct)}%`,
            background: 'linear-gradient(90deg, var(--status-warning), var(--success-green))',
          }}
        />
        <div
          className="absolute -top-1 h-4 w-4 rounded-full border-[3px]"
          style={{
            left: `calc(${realPct}% - 8px)`,
            background: 'var(--card)',
            borderColor: realBand.color,
            boxShadow: 'var(--shadow-warm-sm)',
          }}
        />

        {/* Preview ghost — a translucent, dashed clone that trails the cursor to
            show the band you're inspecting, without implying the score changed. A
            half-transparent gradient fill trails with it up to the hover point. */}
        {previewing && (
          <>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 rounded-full opacity-40 transition-[width] duration-100 ease-out motion-reduce:transition-none"
              style={{
                width: `${Math.max(2, activePct)}%`,
                background: 'linear-gradient(90deg, var(--status-warning), var(--success-green))',
              }}
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-1 h-4 w-4 rounded-full border-2 border-dashed opacity-60 transition-[left] duration-100 ease-out motion-reduce:transition-none"
              style={{
                left: `calc(${activePct}% - 8px)`,
                background: 'color-mix(in srgb, var(--card) 55%, transparent)',
                borderColor: band.color,
              }}
            />
          </>
        )}
      </div>

      {/* Labels — focusable band targets (keyboard parity with hover). */}
      <div className="mt-2 flex justify-between gap-1">
        {BANDS.map((b) => {
          const center = (b.min + b.max) / 2;
          const isActive = band.key === b.key && previewing;
          return (
            <button
              key={b.key}
              type="button"
              onFocus={() => previewAt(center)}
              onBlur={() => setHoverPct(null)}
              onPointerEnter={() => previewAt(center)}
              className={cn(
                // On phones the overline's wide tracking makes these four words
                // too wide for the column — they collide and "ON FIRE" wraps. Keep
                // them on one line and tighten size/tracking, restoring the full
                // overline treatment from sm up.
                'cursor-pointer whitespace-nowrap text-[8.5px] font-bold uppercase leading-none tracking-[0.04em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-overline',
                isActive ? 'text-foreground' : 'text-muted-foreground/70 hover:text-foreground',
              )}
              aria-label={`${b.label}: ${b.desc}`}
            >
              {b.label.toUpperCase()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
